import * as Form from '@radix-ui/react-form';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  ChangeEvent,
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { useEffect, useState } from 'react';

import styles from './text-input.module.scss';

/**
 * Posição do ícone dentro do {@link TextInput}.
 *
 * - `start` — antes do texto digitado
 * - `end` — depois do texto digitado
 */
export type TextInputIconPosition = 'start' | 'end';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: ReactNode;
  /**
   * Classe CSS da caixa do campo: o próprio input ou, quando há `tag` ou
   * `icon`, o wrapper que agrupa os dois.
   */
  className?: string;
  /** Exibe o indicador visual e marca o input como obrigatório. */
  required?: boolean;
  containerStyle?: CSSProperties;
  containerClassName?: string;
  errorMessage?: ReactNode;
  error?: boolean;
  tag?: string;
  labelClassName?: string;
  /**
   * Limite máximo de caracteres aceitos pelo input.
   * Quando definido, exibe o contador `digitados/limite caracteres`.
   */
  maxLength?: number;
  /**
   * Ícone exibido dentro do input.
   * Ex.: `faMagnifyingGlass`, `faClock`, `faCalendar`.
   */
  icon?: IconDefinition;
  /**
   * Posição do ícone dentro do input.
   * @default 'start'
   */
  iconPosition?: TextInputIconPosition;
  /** Cor do ícone. Por padrão acompanha o estado do campo. */
  iconColor?: string;
}

export const TextInput = ({
  name,
  containerClassName,
  containerStyle,
  label,
  type = 'text',
  disabled,
  errorMessage,
  error,
  tag,
  labelClassName,
  maxLength,
  value,
  defaultValue,
  onChange,
  required,
  icon,
  iconPosition = 'start',
  iconColor,
  className,
  ...rest
}: TextInputProps) => {
  const hasWrapper = !!tag || !!icon;
  const isError = error || !!errorMessage;
  const [characterCount, setCharacterCount] = useState(
    () => String(value ?? defaultValue ?? '').length,
  );

  useEffect(() => {
    if (value !== undefined) {
      setCharacterCount(String(value).length);
    }
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCharacterCount(event.target.value.length);
    onChange?.(event);
  };

  const inputProps = {
    id: name,
    disabled,
    type,
    maxLength,
    value,
    defaultValue,
    required,
    onChange: handleChange,
    className: hasWrapper ? undefined : className,
    ...rest,
  };

  const renderIcon = (position: TextInputIconPosition) =>
    icon && iconPosition === position ? (
      <span
        className={styles.icon}
        data-position={position}
        aria-hidden="true"
        data-testid={`${name}-icon`}
      >
        <FontAwesomeIcon icon={icon} color={iconColor} />
      </span>
    ) : null;

  return (
    <Form.Field
      name={name}
      className={`${styles.TextInput} ${containerClassName || ''} ${
        disabled ? styles.disabled : ''
      } ${isError ? styles.error : ''}`}
      style={containerStyle}
    >
      {label ? (
        <Form.Label
          htmlFor={name}
          className={`${styles.label} ${labelClassName || ''}`}
        >
          {label}
          {required && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </Form.Label>
      ) : null}

      {/* O Form.Control precisa ficar no input: o Radix chama setCustomValidity nele */}
      {hasWrapper ? (
        <div
          className={`${styles.inputWrapper} ${className || ''}`}
          data-testid={`${name}-field`}
        >
          {renderIcon('start')}
          {tag ? <span className={styles.tag}>{tag}</span> : null}
          <Form.Control asChild>
            <input {...inputProps} />
          </Form.Control>
          {renderIcon('end')}
        </div>
      ) : (
        <Form.Control asChild>
          <input {...inputProps} />
        </Form.Control>
      )}

      {maxLength !== undefined && (
        <span className={styles.characterCounter}>
          {characterCount}/{maxLength} caracteres
        </span>
      )}

      {errorMessage && (
        <Form.Message className={styles.errorMessage}>
          {errorMessage}
        </Form.Message>
      )}
    </Form.Field>
  );
};
